import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { cloneDeep } from 'lodash';

import { CRMResp } from '@api/responseModels/crm/crmResponse';
import { RequestResult } from '@api/responseModels/requestResult';
import { WorkerPositionResp } from '@api/responseModels/worker/workerPositionResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { ErrorCodes } from '@enums/errorCodes';

import { buildDefaultSchedule } from '../common/helpers';
import { getScheduleMode, SCHEDULE_TYPE_BY_MODE } from '../common/scheduleMode';
import { ScheduleUseCases, TScheduleTemplatePanelState, TState } from '../schedule';
import { useResetEDs } from './useResetEds';

export function useLoadSchedule({
    state,
    stpState,
    crmID,
    workerPositionID,
    useCase,
    messageApi,
}: {
    state: TState;
    stpState: TScheduleTemplatePanelState;
    crmID: number | string;
    workerPositionID: number | string;
    useCase: ScheduleUseCases;
    messageApi: any;
}) {
    const navigate = useNavigate();
    const { resetEDs } = useResetEDs(state);

    const loadSchedule = useCallback(async () => {
        state.isLoading = true;
        try {
            let result: RequestResult<CRMResp | WorkerPositionResp> = null;

            if (useCase === ScheduleUseCases.CRM_Edit || useCase === ScheduleUseCases.CRM_View) {
                result = await CRMAPIManager.request<CRMResp>(async (api) => {
                    return await api.getCRM(+crmID);
                });

                state.schedule = (result.data as CRMResp)?.data?.schedule ?? null;
            } else {
                result = await CRMAPIManager.request<WorkerPositionResp>(async (api) => {
                    return await api.getWorkerPosition(+workerPositionID, +crmID);
                });

                state.schedule = (result.data as WorkerPositionResp)?.data?.schedule;
                state.wpName = result.data.data.name;
            }

            if (state.schedule == null) {
                const isEditCase =
                    useCase === ScheduleUseCases.CRM_Edit ||
                    useCase === ScheduleUseCases.WorkerPosition_Edit;
                // У CRM/рабочего места ещё нет графика — в режиме редактирования открываем пустую форму создания
                if (!isEditCase) throw '404';
                state.schedule = buildDefaultSchedule(+crmID);
            }

            state.scheduleMode = getScheduleMode(state.schedule);
            // графики, сохранённые старой версией фронта, лежат с типом 2, которого на
            // бэке нет: приводим к реальному типу до снимка scheduleImmutable, иначе
            // форма откроется уже «изменённой»
            state.schedule.schedule_type = SCHEDULE_TYPE_BY_MODE[state.scheduleMode];

            state.scheduleOrigin = state.schedule;
            state.scheduleImmutable = cloneDeep(state.schedule);

            if (result.errorCode) throw result.errorCode;
            if (result.statusCode === 404) throw result.statusCode;

            resetEDs();
            stpState.activeTemplateNameMirror = state.schedule.title;
        } catch (err) {
            if (err === ErrorCodes.AccessDenied || err === '404') {
                messageApi.error('График не найден');
                navigate(`/lk/worker/crm/${crmID}`);
            } else {
                messageApi.error('Ошибка при загрузке графика');
                console.error(err);
            }
        } finally {
            state.isLoading = false;
        }
    }, [crmID, workerPositionID, messageApi, navigate, resetEDs, state, stpState, useCase]);

    return { loadSchedule };
}
